import { type ReactElement, useCallback } from 'react'
import { WorkflowDeprecatedCheck, WorkflowDisabledTooltip } from 'app/components/workflow'
import { confirmDelete, useRequiredParams } from '@util'
import { type AccountStatusData, type AccountStatusId } from '@terros/common'
import { useNavigate, useRouter, createFileRoute } from '@tanstack/react-router'
import { notifications } from '@mantine/notifications'
import { useForm, isNotEmpty } from '@mantine/form'
import { useToastError } from '@hooks'
import { useAccountStatuses, useRemoveAccountStatus, useUpdateAccountStatus, useTranslation } from '@front/model'
import {
  ButtonDelete,
  ButtonSave,
  DangerZone,
  NotReady,
  Page,
  TAuditInfoSection,
  TButton,
  TListItem,
} from '@components'
import { fromAccountStatusFormData, toAccountStatusFormData } from '../settings.status_.add/util'
import type { AccountStatusFormData } from '../settings.status_.add/types'
import { AccountStatusForm } from '../settings.status_.add/form'

type RouteProps = {
  statusId: AccountStatusId
}

function EditStatus(): ReactElement {
  const { statusId } = useRequiredParams<RouteProps>()
  const { data, isLoading, error, refetch, isFetching } = useAccountStatuses({ archived: 'all' })

  if (isLoading || error || !data) return <NotReady data={data} error={error} id='Stages' isLoading={isLoading} />

  const status = data.statuses.find((o) => o.statusId === statusId)
  if (!status) return <NotReady data={data} error='Stage not found' id='Stage' />

  if (data.includesDefaults) return <NotReady error='You cannot edit default account stages.' id='Stage' />

  return (
    <WorkflowDeprecatedCheck>
      {({ isWorkflowForCanvassEnabled }) => (
        <EditStatusWithStatusData
          existing={status}
          isFetching={isFetching}
          refetch={refetch}
          isWorkflowEnabled={isWorkflowForCanvassEnabled}
        />
      )}
    </WorkflowDeprecatedCheck>
  )
}

type Props = {
  existing: AccountStatusData
  refetch: VoidFunction
  isFetching: boolean
  isWorkflowEnabled: boolean
}

const EditStatusWithStatusData = ({ existing, refetch, isFetching, isWorkflowEnabled }: Props): ReactElement => {
  const { t } = useTranslation()
  const router = useRouter()
  const navigate = useNavigate()
  const { update, isUpdating, error } = useUpdateAccountStatus()
  const { remove, isRemoving, error: removeError } = useRemoveAccountStatus()
  useToastError(error)
  useToastError(removeError)

  const form = useForm<AccountStatusFormData<AccountStatusData>>({
    initialValues: toAccountStatusFormData(existing),
    validate: {
      name: isNotEmpty('Name is required'),
    },
    validateInputOnChange: true,
  })

  const handleSubmit = useCallback(
    async (unsaved: AccountStatusFormData<AccountStatusData>): Promise<void> => {
      await update(fromAccountStatusFormData(unsaved))
      navigate({ to: '..' })
      form.resetDirty(unsaved)
      notifications.show({ message: 'Saved Stage', color: 'green' })
    },
    [navigate, update, form]
  )

  const handleDelete = useCallback(async () => {
    const confirmed = await confirmDelete({
      title: t('Archive Stage'),
      message: t('Are you sure you want to archive this stage?'),
      label: t('Archive'),
      t,
    })
    if (!confirmed) return

    await remove({ statusId: existing.statusId })
    notifications.show({ message: t('Removed Stage'), color: 'green' })
    router.history.back()
  }, [remove, existing.statusId, t])

  const handleUnarchive = useCallback(async () => {
    await update({ ...existing, isDeleted: false })
    notifications.show({ message: t('Unarchived Stage'), color: 'green' })
    router.history.back()
  }, [update, existing, t])

  const isArchived = existing.isDeleted

  return (
    <form className='w-full' onSubmit={form.onSubmit(handleSubmit)}>
      <Page>
        <Page.Heading back isFetching={isFetching} refetch={refetch} title={t('Edit Stage')}>
          <WorkflowDisabledTooltip
            isWorkflowEnabled={isWorkflowEnabled}
            conditions={[{ active: !form.isDirty(), label: t('No changes to save.') }]}
          >
            <ButtonSave loading={isUpdating} type='submit' />
          </WorkflowDisabledTooltip>
        </Page.Heading>
        <Page.Content scrollArea>
          <AccountStatusForm form={form} />
          <TAuditInfoSection {...existing}>
            <TListItem label={t('Stage ID')} details={existing.statusId} canCopy detailBold />
          </TAuditInfoSection>
          {!isArchived && !isWorkflowEnabled && (
            <DangerZone>
              <ButtonDelete loading={isRemoving} onClick={handleDelete}>
                {t('Archive')}
              </ButtonDelete>
            </DangerZone>
          )}
          {isArchived && !isWorkflowEnabled && (
            <TButton variant='secondary' onClick={handleUnarchive}>
              {t('Unarchive')}
            </TButton>
          )}
        </Page.Content>
      </Page>
    </form>
  )
}

export const Route = createFileRoute('/settings/status_/$statusId/')({
  component: EditStatus,
})
